import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { formatINR } from "@/lib/constants";

export default function ExecutionTab({ clientId, canEdit, reload }) {
  const [tasks, setTasks] = useState([]);
  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/execution-tasks`);
    setTasks(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const move = async (task, newStatus) => {
    try {
      await api.patch(`/execution-tasks/${task.id}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      load(); reload && reload();
    } catch { toast.error("Failed"); }
  };

  const columns = ["Pending", "In Progress", "Submitted", "Confirmed"];
  const groups = Object.fromEntries(columns.map((c) => [c, tasks.filter((t) => t.status === c)]));

  return (
    <div className="space-y-6">
      {tasks.length === 0 && <p className="text-sm text-muted-foreground">No execution tasks yet. Tasks appear automatically once the client approves a written plan.</p>}
      <div className="grid gap-4 md:grid-cols-4">
        {columns.map((col, idx) => (
          <div key={col} className="rounded-lg border border-border bg-white p-4" data-testid={`exec-col-${col.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="mb-3 flex items-center justify-between">
              <div className="font-dashboard text-sm font-bold">{col}</div>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{groups[col].length}</span>
            </div>
            <div className="space-y-2">
              {groups[col].map((t) => (
                <div key={t.id} className="rounded-md border border-border bg-secondary/30 p-3 text-sm" data-testid={`exec-task-${t.id}`}>
                  <div className="mb-1 font-medium">{t.category}</div>
                  {t.fund_name && <div className="mb-0.5 text-xs font-semibold text-primary">{t.fund_name}</div>}
                  <div className="text-xs text-muted-foreground">{t.mf_sub_category || t.instrument_type}</div>
                  <div className="mt-1 text-xs">{formatINR(t.target_amount)}</div>
                  {canEdit && idx < columns.length - 1 && (
                    <Button size="sm" variant="outline" className="mt-2 h-7 gap-1 text-xs"
                      onClick={() => move(t, columns[idx + 1])} data-testid={`advance-task-${t.id}`}>
                      Move to {columns[idx + 1]} <ArrowRight className="h-3 w-3" />
                    </Button>
                  )}
                  {t.status === "Confirmed" && <CheckCircle2 className="mt-2 h-4 w-4 text-primary" />}
                </div>
              ))}
              {groups[col].length === 0 && <p className="text-xs text-muted-foreground">Empty</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
